import React, { useState, useEffect } from 'react';
import { Container, Box, TextField, Button, Typography, Tabs, Tab } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import RawHtmlEditor from '../components/Editor/RawHtmlEditor';
import EmailClientSimulator from '../components/EmailClientSimulator'; // optional, if you still want client simulation
import ColorWheel from '../components/Editor/ColorWheel';
import templateService from '../services/templateService';
import sesService from '../services/sesService';

// Style overrides for TextField components to display white text and white borders
const textFieldStyles = {
  '& .MuiInputLabel-root': { color: '#fff' },
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': {
      borderColor: '#fff',
    },
    '&:hover fieldset': {
      borderColor: '#fff',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#fff',
    },
  },
};

const TemplateEditorPage = () => {
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('id');

  const [template, setTemplate] = useState({
    name: '',
    subject: '',
    htmlContent: '',
    textContent: '',
    backgroundColor: '#ffffff',
  });
  const [errorMsg, setErrorMsg] = useState('');
  const [testEmail, setTestEmail] = useState('');

  // Toggle between raw HTML and client simulation
  const [tabIndex, setTabIndex] = useState(0);
  const [showClientSimulator, setShowClientSimulator] = useState(false);

  useEffect(() => {
    if (templateId) {
      const fetchTemplate = async () => {
        try {
          const data = await templateService.getTemplateById(templateId);
          if (!data) {
            setErrorMsg('Template not found.');
          } else {
            setTemplate(data);
          }
        } catch (err) {
          console.error('Error fetching template:', err);
          setErrorMsg('Error fetching template.');
        }
      };
      fetchTemplate();
    }
  }, [templateId]);

  const handleColorChange = (color) => {
    setTemplate({ ...template, backgroundColor: color });
  };

  const handleUpdate = (updatedTemplate) => {
    setTemplate(updatedTemplate);
  };

  const handleSave = async () => {
    try {
      if (templateId) {
        await templateService.updateTemplate(template);
      } else {
        await templateService.createTemplate(template);
      }
      window.location.href = '/';
    } catch (err) {
      console.error('Error saving template:', err);
      setErrorMsg('Error saving template.');
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      alert('Please enter a test email address.');
      return;
    }
    try {
      await sesService.sendTestEmail(template, testEmail);
      alert('Test email sent successfully!');
    } catch (err) {
      console.error('Error sending test email:', err);
      alert('Error sending test email.');
    }
  };

  return (
    <Container
      sx={{
        mt: 4,
        backgroundColor: '#000000',
        color: '#ffffff',
        p: 2,
        borderRadius: 2,
      }}
    >
      {errorMsg && <Typography color="error">{errorMsg}</Typography>}

      {/* Template Details */}
      <Box sx={{ mb: 2 }}>
        <TextField
          label="Template Name"
          value={template.name}
          onChange={(e) => setTemplate({ ...template, name: e.target.value })}
          fullWidth
          sx={{ ...textFieldStyles, mb: 2 }}
        />
        <TextField
          label="Subject Line"
          value={template.subject}
          onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
          fullWidth
          sx={textFieldStyles}
        />
      </Box>

      {/* Color Picker */}
      <ColorWheel color={template.backgroundColor} onChange={handleColorChange} />

      {/* Test Email Section */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Test Recipient Email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          variant="outlined"
          size="small"
          sx={textFieldStyles}
        />
        <Button variant="contained" color="primary" onClick={handleSendTestEmail}>
          Send Test Email
        </Button>
      </Box>

      {/* Toggle Client Simulation */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button variant="outlined" onClick={() => setShowClientSimulator(!showClientSimulator)}>
          {showClientSimulator ? 'Hide Client Simulation' : 'Show Client Simulation'}
        </Button>
      </Box>

      {/* Tabs for Raw HTML vs Client Simulation */}
      <Tabs
        value={tabIndex}
        onChange={(e, newValue) => setTabIndex(newValue)}
        sx={{ mt: 2, color: '#fff' }}
      >
        <Tab label="Raw HTML" sx={{ color: '#fff' }} />
        <Tab label="Client Simulation" disabled={!showClientSimulator} sx={{ color: '#fff' }} />
      </Tabs>

      {tabIndex === 0 && (
        <RawHtmlEditor template={template} onUpdate={handleUpdate} />
      )}
      {tabIndex === 1 && showClientSimulator && (
        <EmailClientSimulator htmlContent={template.htmlContent} />
      )}

      {/* Save Template Button */}
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" color="primary" onClick={handleSave}>
          Save Template
        </Button>
      </Box>
    </Container>
  );
};

export default TemplateEditorPage;
